import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { useNavigate } from 'react-router-dom'
import SimpleBar from 'simplebar-react'
import { XClose, DownloadCloud01, Building07 } from '@untitled-ui/icons-react'
import {
  MyAutocomplete,
  MyButton,
  MyCardFile,
  MyDropzone,
  MyModalSlider,
  myToaster,
} from '@interstellar-component'
import { formatFileSize, myToasterFromApi } from '@src/services/Helper'
import {
  CompanyPickerOption,
  isCompanySelectable,
  toCompanyOption,
} from '@pages/Company/components/MyMemberStatusChip'
import Service from '../service'
import { XLSX_ACCEPT, XLSX_MAX_BYTES } from '../constants'
import Notice from './Notice'

// Upload one filled template for one company. The backend validates and stages
// every row (no enquiry, no charge, no email yet) and answers with the new
// batch, which this slider opens. companyId: the company to start with (a
// batch page's "Upload a new batch"), when it is one the user may pick.
function NewBulkEnquirySlider({ open, onClose, companyId = null }) {
  const navigate = useNavigate()
  const [companies, setCompanies] = useState(null) // null until the list is read
  const [company, setCompany] = useState(null)
  const [file, setFile] = useState(null)
  const [error, setError] = useState(null)
  const [downloading, setDownloading] = useState(false)
  const [submitting, setSubmitting] = useState(false)

  useEffect(() => {
    if (!open) return undefined
    let active = true
    setCompanies(null)
    setCompany(null)
    setFile(null)
    setError(null)
    Service.getCompanyOptions()
      .then((res) => {
        if (!active) return
        const options = (res.data || []).map(toCompanyOption)
        setCompanies(options)
        const selectable = options.filter(isCompanySelectable)
        const asked = companyId ? selectable.find((option) => option.value === companyId) : null
        if (asked) setCompany(asked)
        else if (selectable.length === 1) setCompany(selectable[0])
      })
      // A failed read is toasted; the "no active company" hint stays hidden.
      .catch((err) => active && myToasterFromApi(err))
    return () => {
      active = false
    }
  }, [open, companyId])

  const noSelectableCompany = companies !== null && !companies.some(isCompanySelectable)

  const handleDownloadTemplate = async () => {
    setDownloading(true)
    try {
      await Service.downloadTemplate(company.value)
    } catch (err) {
      myToasterFromApi(err)
    } finally {
      setDownloading(false)
    }
  }

  const handleSubmit = async () => {
    setSubmitting(true)
    setError(null)
    try {
      const formData = new FormData()
      formData.append('company_id', company.value)
      formData.append('file', file)
      const res = await Service.upload(formData)
      myToaster(res)
      onClose()
      navigate(`/bulk-enquiry/${res.data.id}`, {
        state: { uploadWarnings: res.data.warnings || [] },
      })
    } catch (err) {
      // The backend's message already names the problem (old template, wrong
      // header, too many rows, company not active, ...).
      setError(err?.message || 'The file could not be uploaded.')
    } finally {
      setSubmitting(false)
    }
  }

  const panel = (
    <div className="flex h-screen w-[440px] flex-col bg-white shadow-xl">
      <header className="relative flex items-start gap-x-4 border-b border-gray-100 px-6 py-6">
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          className="absolute right-[12px] top-[12px] flex h-10 w-10 items-center justify-center rounded-lg p-2 text-gray-400 hover:bg-gray-50 active:bg-gray-100"
        >
          <XClose size={24} stroke="currentColor" />
        </button>
        <div className="flex flex-1 flex-col gap-1 pt-1">
          <p className="text-lg font-semibold text-gray-900">New bulk upload</p>
          <p className="text-sm text-gray-600">
            Upload many enquiries for one company. Rows are checked first; nothing is sent or charged yet.
          </p>
          <p className="text-xs text-gray-500">
            Each row is charged when you process the batch, as a single New Enquiry: quota first, then saldo.
          </p>
        </div>
      </header>

      <section className="flex-1 overflow-hidden">
        <SimpleBar forceVisible="y" style={{ maxHeight: '100%' }}>
          <div className="flex flex-col gap-6 px-6 py-6">
            <div className="flex flex-col gap-1.5">
              <p className="text-sm font-medium text-gray-700">
                Company <span className="text-brand/700">*</span>
              </p>
              <MyAutocomplete
                name="company"
                options={companies || []}
                value={company}
                placeholder="Select company"
                startAdornment={<Building07 className="size-4 text-gray-400" />}
                onChange={(_e, value) => {
                  setCompany(value)
                  setError(null)
                }}
                isOptionEqualToValue={(opt, val) => opt?.value === val?.value}
                getOptionLabel={(opt) => opt?.label || ''}
                getOptionDisabled={(opt) => !isCompanySelectable(opt)}
                renderOption={(opt) => <CompanyPickerOption company={opt} label={opt?.label} />}
              />
              {noSelectableCompany && (
                <p className="text-xs text-gray-500">
                  No active company is available to your account, so nothing can be uploaded yet.
                </p>
              )}
            </div>

            <div className="flex flex-col gap-1.5">
              <MyButton
                type="button"
                color="secondary"
                variant="outlined"
                size="md"
                customClassname="w-max gap-2"
                disabled={!company || downloading}
                onClick={handleDownloadTemplate}
              >
                <DownloadCloud01 className="size-4" />
                {downloading ? 'Downloading...' : 'Download template'}
              </MyButton>
              <p className="text-xs text-gray-500">
                The template is made for the selected company (its levels and consent settings). Use a
                fresh one for every upload.
              </p>
            </div>

            {/*
              The file shown is the file uploaded. MyDropzone rewrites every
              .xlsx it lists (all cells turned into text, formats dropped: a
              different file, another size), and the backend needs the workbook
              untouched: it reads each cell's real type (a NIK stored as a
              number is refused, a date cell is a date). So the dropzone only
              picks: react-dropzone's onDropAccepted carries the original File,
              which is kept, and the dropzone gives way to a card of that File.
              Removing it brings back an empty dropzone and clears the error.
            */}
            {file ? (
              <MyCardFile
                file={file}
                progress={null}
                showImage={false}
                onDeleteFile={() => {
                  setFile(null)
                  setError(null)
                }}
              />
            ) : (
              <MyDropzone
                accept={XLSX_ACCEPT}
                maxSize={XLSX_MAX_BYTES}
                onDropAccepted={(accepted) => {
                  setFile(accepted[0] || null)
                  setError(null)
                }}
                onDropRejected={() =>
                  setError(`Choose one .xlsx file of at most ${formatFileSize(XLSX_MAX_BYTES)}.`)
                }
              />
            )}

            {error && (
              <Notice tone="error" title="Upload failed">
                <p>{error}</p>
              </Notice>
            )}
          </div>
        </SimpleBar>
      </section>

      <footer className="flex items-center justify-end gap-3 border-t border-gray-200 px-6 py-4">
        <MyButton type="button" color="secondary" variant="outlined" size="md" onClick={onClose}>
          Cancel
        </MyButton>
        <MyButton
          type="button"
          color="primary"
          variant="filled"
          size="md"
          disabled={!company || !file || submitting}
          onClick={handleSubmit}
        >
          {submitting ? 'Uploading...' : 'Upload'}
        </MyButton>
      </footer>
    </div>
  )

  return <MyModalSlider open={open} scrim element={panel} onClose={onClose} />
}

NewBulkEnquirySlider.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  companyId: PropTypes.string,
}

export default NewBulkEnquirySlider
